import { showAlert } from '@/components/ui/alert';
import { STRINGS } from '@/constants/strings';
import { MutateCoachForm, mutateCoachSchema } from '@/features/coach/schema/mutate-coach-schema';
import {
  convertCoachDocToMutateCoachForm,
  convertMutateCoachFormToCoachUpdate,
} from '@/features/coach/utils';
import { useCoachQuery } from '@/features/home/queries';
import { homeKeys } from '@/features/home/queries/query-keys';
import { CoachDoc } from '@/features/home/services/get-coach';
import { ID_TOKEN_REVOKED } from '@/libs/api/interceptors';
import { ApiError } from '@/libs/api/types';
import { shouldSuppressErrorAlert } from '@/libs/errors/request-timeout';
import { updateCoachCollection, WithId } from '@/libs/firebase/firestore';
import { uploadImageToStorage } from '@/libs/firebase/storage/storage-helpers';
import { useSessionStore } from '@/stores/session-store';
import { useUiStore } from '@/stores/ui-store';
import { shouldUploadAvatarUri } from '@/utils/helper';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigation } from '@react-navigation/native';
import { useQueryClient } from '@tanstack/react-query';
import parsePhoneNumberFromString, { CountryCode } from 'libphonenumber-js';
import { useCallback, useEffect, useEffectEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useShallow } from 'zustand/shallow';

const INITIAL_MUTATE_INFO_AVATAR_FORM = {
  avatar: undefined,
  phoneNumber: '',
  streetAddress: '',
  streetAddress2: '',
  city: '',
  state: '',
  zipCode: '',
};

export function useMutateCoach() {
  const uid = useSessionStore(useShallow(state => state.user?.uid));
  const { showGlobalLoading, hideGlobalLoading } = useUiStore();
  const { data: coach } = useCoachQuery(uid);
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const form = useForm<MutateCoachForm, any, MutateCoachForm>({
    defaultValues: INITIAL_MUTATE_INFO_AVATAR_FORM,
    resolver: yupResolver(mutateCoachSchema()),
  });

  const mode = 'edit' as const;

  const setCoachDocToForm = useEffectEvent((_coach: WithId<CoachDoc> | null | undefined) => {
    if (!uid) return;
    if (_coach) {
      form.reset(convertCoachDocToMutateCoachForm(_coach));
      return;
    }
  });

  useEffect(() => {
    setCoachDocToForm(coach);
  }, [coach]);

  const onSave = useCallback(async () => {
    const handleFormSubmit = form.handleSubmit(async data => {
      let onSuccess: (() => void) | null = null;
      try {
        showGlobalLoading();
        if (!coach?.id) return;

        const phoneNumberParse = parsePhoneNumberFromString(
          data.phoneNumber ?? '',
          data.countryCode as CountryCode | undefined,
        );
        const payload = convertMutateCoachFormToCoachUpdate({
          ...data,
          phoneNumber: phoneNumberParse?.formatInternational() ?? '',
        });

        await updateCoachCollection(coach.id, payload);
        if (shouldUploadAvatarUri(data.avatar?.uri)) {
          await uploadImageToStorage(data.avatar.uri, `profile-pictures/${coach.id}.jpg`);
        }
        queryClient.invalidateQueries({ queryKey: homeKeys.coach(coach.id) });
        onSuccess = () => {
          navigation.goBack();
          showAlert({
            message: STRINGS.player.mutatePlayer.footer.updateSuccessMessage,
            buttons: [{ text: STRINGS.dismiss }],
          });
        };
      } catch (error) {
        if (
          !shouldSuppressErrorAlert(error) &&
          !(
            (error as ApiError)?.serverCode === ID_TOKEN_REVOKED &&
            (error as ApiError)?.status === 401
          )
        ) {
          showAlert({
            title: STRINGS.mutatePlayer.footer.noticeTitle,
            message: STRINGS.mutatePlayer.footer.errorForm,
          });
        }
      } finally {
        hideGlobalLoading();
        if (onSuccess) {
          onSuccess();
        }
      }
    });
    handleFormSubmit();
  }, [coach?.id, form, hideGlobalLoading, navigation, queryClient, showGlobalLoading]);

  return {
    form,
    onSave,
    mode,
  };
}
